import { defineStore } from "pinia"
import { ref } from "vue"
import type { IconName } from "@/components/icons/registry"

export enum ToastType {
	Success = "success",
	Error = "error",
}

interface ToastAction {
	labelKey: string
	onClick: () => void
}

export interface Toast {
	id: number
	type: ToastType
	messageKey: string
	params?: Record<string, unknown>
	duration: number
	action?: ToastAction
	icon?: IconName
}

interface ToastOptions {
	params?: Record<string, unknown>
	duration?: number
	action?: ToastAction
	icon?: IconName
}

const DEFAULT_DURATION: Record<ToastType, number> = {
	[ToastType.Success]: 4000,
	[ToastType.Error]: 6000,
}

export const useToastStore = defineStore("toast", () => {
	const toasts = ref<Toast[]>([])
	let nextId = 0

	const push = (type: ToastType, messageKey: string, options: ToastOptions = {}) => {
		const toast: Toast = {
			id: nextId++,
			type,
			messageKey,
			params: options.params,
			duration: options.duration ?? DEFAULT_DURATION[type],
			action: options.action,
			icon: options.icon,
		}
		toasts.value.push(toast)
		return toast.id
	}

	const success = (messageKey: string, options?: ToastOptions) => push(ToastType.Success, messageKey, options)
	const error = (messageKey: string, options?: ToastOptions) => push(ToastType.Error, messageKey, options)

	const dismiss = (id: number) => {
		toasts.value = toasts.value.filter(toast => toast.id !== id)
	}

	return {
		toasts,
		success,
		error,
		dismiss,
	}
})
